// src/services/blogService.js
import { database } from '../firebase';
import { 
  ref, 
  push, 
  set, 
  get, 
  remove, 
  query, 
  orderByChild, 
  equalTo,
  serverTimestamp,
  onValue,
  off
} from 'firebase/database';

class BlogService {
  constructor() {
    this.blogsRef = ref(database, 'blogs');
  }

  // Generate URL-friendly slug from title
  generateSlug(title) {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  }

  // Calculate estimated reading time
  calculateReadTime(content) {
    const wordsPerMinute = 200;
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  }

  // Create a new blog post
  async createBlog(blogData) {
    try {
      const slug = this.generateSlug(blogData.title);
      const readTime = this.calculateReadTime(blogData.content);
      
      const newBlogRef = push(this.blogsRef);
      const blogId = newBlogRef.key;
      
      const blogPost = {
        id: blogId,
        title: blogData.title,
        content: blogData.content,
        excerpt: blogData.excerpt || this.generateExcerpt(blogData.content),
        featuredImage: blogData.featuredImage || '',
        tags: blogData.tags || [],
        author: blogData.author || 'Admin',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt: blogData.isPublished ? serverTimestamp() : null,
        isPublished: blogData.isPublished || false,
        slug: slug,
        metaDescription: blogData.metaDescription || this.generateExcerpt(blogData.content),
        readTime: readTime
      };

      await set(newBlogRef, blogPost);
      return { success: true, id: blogId, data: blogPost };
    } catch (error) {
      console.error('Error creating blog:', error);
      return { success: false, error: error.message };
    }
  }

  // Update an existing blog post
  async updateBlog(blogId, blogData) {
    try {
      const blogRef = ref(database, `blogs/${blogId}`);
      const slug = this.generateSlug(blogData.title);
      const readTime = this.calculateReadTime(blogData.content);
      
      const updatedBlog = {
        ...blogData,
        updatedAt: serverTimestamp(),
        slug: slug,
        readTime: readTime,
        publishedAt: blogData.isPublished && !blogData.publishedAt ? serverTimestamp() : blogData.publishedAt
      };

      await set(blogRef, updatedBlog);
      return { success: true, data: updatedBlog };
    } catch (error) {
      console.error('Error updating blog:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete a blog post
  async deleteBlog(blogId) {
    try {
      const blogRef = ref(database, `blogs/${blogId}`);
      await remove(blogRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting blog:', error);
      return { success: false, error: error.message };
    }
  }

  // Get a single blog post by ID
  async getBlog(blogId) {
    try {
      const blogRef = ref(database, `blogs/${blogId}`);
      const snapshot = await get(blogRef);
      
      if (snapshot.exists()) {
        return { success: true, data: snapshot.val() };
      } else {
        return { success: false, error: 'Blog not found' };
      }
    } catch (error) {
      console.error('Error getting blog:', error);
      return { success: false, error: error.message };
    }
  }

  // Get blog by slug (without Firebase indexing)
  async getBlogBySlug(slug) {
    try {
      // Get all blogs and filter by slug in memory to avoid Firebase index requirement
      const snapshot = await get(this.blogsRef);
      
      if (snapshot.exists()) {
        const blogs = snapshot.val();
        const blogArray = Object.values(blogs);
        const blog = blogArray.find(b => b.slug === slug);
        
        if (blog) {
          return { success: true, data: blog };
        } else {
          return { success: false, error: 'Blog not found' };
        }
      } else {
        return { success: false, error: 'Blog not found' };
      }
    } catch (error) {
      console.error('Error getting blog by slug:', error);
      return { success: false, error: error.message };
    }
  }

  // Get all blog posts with optional filters
  async getAllBlogs(filters = {}) {
    try {
      // Fetch all blogs without indexing to avoid Firebase index requirement
      const snapshot = await get(this.blogsRef);
      
      if (snapshot.exists()) {
        const blogs = snapshot.val();
        let blogArray = Object.values(blogs);
        
        // Apply filters in memory
        if (filters.isPublished !== undefined) {
          blogArray = blogArray.filter(blog => blog.isPublished === filters.isPublished);
        }
        
        // Sort by creation date (newest first)
        blogArray.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB - dateA;
        });
        
        return { success: true, data: blogArray };
      } else {
        return { success: true, data: [] };
      }
    } catch (error) {
      console.error('Error getting all blogs:', error);
      return { success: false, error: error.message };
    }
  }

  // Get only published blogs for public display
  async getPublishedBlogs() {
    return this.getAllBlogs({ isPublished: true });
  }

  // Toggle publish status
  async togglePublishStatus(blogId) {
    try {
      const blogResult = await this.getBlog(blogId);
      if (!blogResult.success) {
        return blogResult;
      }
      
      const blog = blogResult.data;
      const newStatus = !blog.isPublished;
      
      const updateData = {
        ...blog,
        isPublished: newStatus,
        publishedAt: newStatus ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
      };
      
      return this.updateBlog(blogId, updateData);
    } catch (error) {
      console.error('Error toggling publish status:', error);
      return { success: false, error: error.message };
    }
  }

  // Real-time listener for blogs
  onBlogsChange(callback, filters = {}) {
    // Listen to all blogs and filter in memory to avoid indexing issues
    const unsubscribe = onValue(this.blogsRef, (snapshot) => {
      if (snapshot.exists()) {
        const blogs = snapshot.val();
        let blogArray = Object.values(blogs);
        
        // Apply filters in memory
        if (filters.isPublished !== undefined) {
          blogArray = blogArray.filter(blog => blog.isPublished === filters.isPublished);
        }
        
        // Sort by creation date (newest first)
        blogArray.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB - dateA;
        });
        
        callback({ success: true, data: blogArray });
      } else {
        callback({ success: true, data: [] });
      }
    }, (error) => {
      console.error('Error in real-time listener:', error);
      callback({ success: false, error: error.message });
    });
    
    return unsubscribe;
  }

  // Remove real-time listener
  offBlogsChange(query) {
    off(query);
  }

  // Generate excerpt from content
  generateExcerpt(content, maxLength = 150) {
    // Remove HTML tags
    const textContent = content.replace(/<[^>]*>/g, '');
    
    if (textContent.length <= maxLength) {
      return textContent;
    }
    
    // Find the last complete word within the limit
    const truncated = textContent.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    return truncated.substring(0, lastSpaceIndex) + '...';
  }

  // Search blogs by title and content
  async searchBlogs(searchTerm, filters = {}) {
    try {
      const allBlogsResult = await this.getAllBlogs(filters);
      
      if (!allBlogsResult.success) {
        return allBlogsResult;
      }
      
      const searchTermLower = searchTerm.toLowerCase();
      const filteredBlogs = allBlogsResult.data.filter(blog => {
        const titleMatch = blog.title.toLowerCase().includes(searchTermLower);
        const contentMatch = blog.content.toLowerCase().includes(searchTermLower);
        const tagsMatch = blog.tags.some(tag => tag.toLowerCase().includes(searchTermLower));
        
        return titleMatch || contentMatch || tagsMatch;
      });
      
      return { success: true, data: filteredBlogs };
    } catch (error) {
      console.error('Error searching blogs:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new BlogService();